import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ShieldAlert, UserPlus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { UserListItem } from '@/api/admin-types'
import { branchListQueryOptions } from '@/api/reference-api'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ADMIN_PERMISSIONS, useAdminPermissions } from '@/features/admin/permissions'
import { useAdminUserList } from '@/features/admin/queries'
import { branchLabel } from '@/lib/branch'
import { formatDateTime } from '@/lib/datetime'

import { DEFAULT_PAGE_SIZE, type AdminUserListSearch } from './search'

/** Long enough that a name typed at speed is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<UserListItem>()

const columns: DataTableColumn<UserListItem>[] = helper.columns([
  helper.accessor('username', {
    header: 'Username',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.username}</span>,
    meta: { noWrap: true, widthClass: 'w-40', card: 'lead' },
  }),
  helper.accessor('fullName', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => <span className="font-medium text-heading">{context.row.original.fullName}</span>,
    meta: { card: 'title' },
  }),
  helper.accessor('employeeCode', {
    header: 'Employee code',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.employeeCode}</span>,
    meta: { noWrap: true },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
  helper.accessor('roles', {
    header: 'Roles',
    enableSorting: false,
    cell: (context) => {
      const roles = context.row.original.roles
      return roles.length === 0 ? (
        <span className="text-xs text-muted">None</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {roles.map((role) => (
            <Badge key={role} tone="brand">
              {role}
            </Badge>
          ))}
        </div>
      )
    },
  }),
  helper.accessor('mfaEnrolled', {
    header: 'MFA',
    enableSorting: false,
    cell: (context) => (
      <Badge tone={context.row.original.mfaEnrolled ? 'success' : 'neutral'}>
        {context.row.original.mfaEnrolled ? 'Enrolled' : 'Not enrolled'}
      </Badge>
    ),
    meta: { widthClass: 'w-32' },
  }),
  helper.accessor('lastLoginUtc', {
    header: 'Last sign-in',
    enableSorting: false,
    cell: (context) => {
      const value = context.row.original.lastLoginUtc
      return <span className="text-xs">{value ? formatDateTime(value) : 'Never'}</span>
    },
    meta: { noWrap: true },
  }),
])

/**
 * The staff directory.
 *
 * The same keyboard-first, URL-driven shape `customer-list.tsx` uses, for the same reason: this is
 * a screen an administrator returns to and filters repeatedly, and a filtered view is worth a link.
 */
export function AdminUsersPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/authenticated/admin/users' })
  const permissions = useAdminPermissions()

  const branches = useQuery(branchListQueryOptions())
  const [searchText, setSearchText] = useState(search.search ?? '')

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const updateSearch = (patch: Partial<AdminUserListSearch>, replace = false) => {
    void navigate({
      to: '/admin/users',
      search: {
        search: search.search,
        status: search.status,
        branchId: search.branchId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    })
  }

  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      void navigate({
        to: '/admin/users',
        search: {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          branchId: search.branchId,
          page: undefined,
          pageSize: search.pageSize,
        },
        replace: true,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [searchText, search.search, search.status, search.branchId, search.pageSize, navigate])

  const query = useAdminUserList({
    search: search.search,
    status: search.status,
    branchId: search.branchId,
    page,
    pageSize,
  })

  const openUser = (user: UserListItem) => {
    void navigate({ to: '/admin/users/$userId', params: { userId: user.userId } })
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(search.search ?? search.status ?? search.branchId)

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    void navigate({ to: '/admin/users', search: { pageSize: search.pageSize } })
  }

  return (
    <RequiresWiderScreen
      screen="The user directory"
      reason="Status, roles, and branch access are read and set together here, and the table is dense — this is a desk screen, not one for a phone in a queue."
    >
      <PageHeader
        title="Users"
        description="Staff accounts, role assignment, branch access, and two-factor enrolment."
        actions={
          permissions.canCreateUser ? (
            <Button asChild variant="primary">
              <Link to="/admin/users/new">
                <UserPlus />
                New user
              </Link>
            </Button>
          ) : null
        }
      />

      {!permissions.canViewUsers ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <ShieldAlert className="size-6 text-warning-text" aria-hidden />
          <p className="text-sm font-medium text-heading">You cannot read the user directory</p>
          <p className="max-w-lg text-xs text-muted">
            This needs <span className="font-mono">{ADMIN_PERMISSIONS.userView}</span>.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-48 flex-1 space-y-1.5">
              <label htmlFor="admin-user-search" className="text-xs font-medium text-body">
                Search
              </label>
              <Input
                id="admin-user-search"
                value={searchText}
                autoComplete="off"
                placeholder="Name, username, or employee code"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>

            <div className="w-40 space-y-1.5">
              <label htmlFor="admin-user-status" className="text-xs font-medium text-body">
                Status
              </label>
              <Select
                value={search.status ?? '__any__'}
                onValueChange={(value) => {
                  updateSearch({ status: value === '__any__' ? undefined : (value as AdminUserListSearch['status']), page: undefined })
                }}
              >
                <SelectTrigger id="admin-user-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Locked">Locked</SelectItem>
                  <SelectItem value="Disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-48 space-y-1.5">
              <label htmlFor="admin-user-branch" className="text-xs font-medium text-body">
                Branch
              </label>
              <Select
                value={search.branchId ?? '__any__'}
                onValueChange={(value) => {
                  updateSearch({ branchId: value === '__any__' ? undefined : value, page: undefined })
                }}
              >
                <SelectTrigger id="admin-user-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any branch</SelectItem>
                  {(branches.data ?? []).map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branchLabel(branch)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters ? (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
          </div>

          <DataTable
            columns={columns}
            data={query.data?.items}
            rowCount={query.data?.totalCount}
            pagination={{ pageIndex: page - 1, pageSize }}
            onPaginationChange={onPaginationChange}
            sorting={UNSORTED}
            onSortingChange={ignoreSortingChange}
            isLoading={query.isPending}
            isFetching={query.isFetching}
            error={query.isError ? query.error : null}
            onRetry={() => void query.refetch()}
            getRowId={(row) => row.userId}
            onRowActivate={openUser}
            pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
            emptyTitle={hasActiveFilters ? 'No accounts match' : 'No accounts yet'}
            emptyDescription={
              hasActiveFilters
                ? 'Nothing was found with these filters.'
                : 'No staff account exists at the branches you can see.'
            }
            emptyAction={
              hasActiveFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear the filters
                </Button>
              ) : permissions.canCreateUser ? (
                <Button asChild variant="primary" size="sm">
                  <Link to="/admin/users/new">
                    <UserPlus />
                    New user
                  </Link>
                </Button>
              ) : null
            }
          />
        </div>
      )}
    </RequiresWiderScreen>
  )
}
